import { useState } from "react";
import { useSignupMutation } from "../redux/services/authApi";
import { useDispatch } from "react-redux";
import { clearAuthUser, setAuthUser } from "../redux/slices/authSlice";
import { setProfileField } from "../redux/slices/profileSlice";
import Cookies from "js-cookie";

export const useCreateAccount = (
  account_type_id?: string,
  initialValues?: { fullName?: string; email?: string; password?: string },
  account_type_name?: string
) => {
  const [signup, { isLoading }] = useSignupMutation();
  const dispatch = useDispatch();
  const [fullName, setFullName] = useState(initialValues?.fullName || "");
  const [email, setEmail] = useState(initialValues?.email || "");
  const [password, setPassword] = useState(initialValues?.password || "");
  const [message, setMessage] = useState<any>("");

  const handleSignup = async (
    agreedToTerms: boolean = false
  ): Promise<{
    success: boolean;
    email?: string;
    // Signed up before but never verified: a fresh code was emailed, no token issued.
    returning?: boolean;
    // Email already verified: they should log in instead.
    alreadyRegistered?: boolean;
    message?: string;
  }> => {
    if (!account_type_id) {
      setMessage("Account type is missing. Please go back and select a type.");
      return { success: false };
    }

    if (!fullName.trim() || !email.trim() || !password.trim()) {
      setMessage("All fields are required");
      return { success: false };
    }

    try {
      setMessage("");
      const response: any = await signup({
        // Trim and collapse repeated spaces so the stored name is clean
        // ("Acme  Ltd " -> "Acme Ltd").
        name: fullName.trim().replace(/\s+/g, " "),
        email,
        password,
        password_confirmation: password,
        account_type_id,
        terms_conditions: agreedToTerms,
      }).unwrap();

      if (response.data?.is_returning) {
        // No user or token comes back for a returning sign-up. Drop anything left over
        // from an earlier attempt so it can't be mistaken for a logged-in session.
        Cookies.remove("token");
        Cookies.remove("signup_user_id");
        dispatch(clearAuthUser());
        return { success: true, email, returning: true, message: response.data.message };
      }

      const user = response.data?.user;
      const token = response.data?.token || response.token;

      if (!user || !token) {
        setMessage("Signup failed: invalid response");
        return { success: false };
      }

      // save token to cookies
      Cookies.set("token", token);

      // save user info in auth slice
      const isOrg = account_type_name?.toLowerCase() === "organization";

      dispatch(
        setAuthUser({
          token,
          user: {
            id: user.id,
            name: user.name,
            email: user.email,
            account_type: isOrg ? "company" : "individual",
            branches: isOrg ? [] : undefined,
            is_head_of_org: isOrg,
          },
        })
      );

      // company type
      dispatch(
        setProfileField({
          field: "company_type",
          value: isOrg ? "company" : "individual",
        })
      );

      return { success: true, email };
    } catch (err: any) {
      if (err?.status === 409 && err?.data?.error?.code === "ALREADY_REGISTERED") {
        return {
          success: false,
          alreadyRegistered: true,
          message: err.data.error.message || "This email is already registered. Please log in instead.",
        };
      }
      if (err?.status === 429) {
        setMessage("Too many attempts. Please wait a minute and try again.");
        return { success: false };
      }
      setMessage(err?.data?.errors || err?.data?.error?.message || "Signup failed");

      return { success: false };
    }
  };

  return {
    fullName,
    setFullName,
    email,
    setEmail,
    password,
    setPassword,
    message,
    isLoading,
    handleSignup,
  };
};